import { AnyFunc, createEventEmitter, Error, EventEmitter, eventObject, Logger } from '@quickplay/foundation';
import {
  Action,
  Ad,
  AdBreak,
  AdComposedPlayer,
  AdContentTimePosition,
  AdEvent,
  AdPlaybackPolicy,
  AdPlaybackPolicyHandler,
  AdPlayer,
  PLAYBACK_STATES,
  PlaybackAutoSeekMode,
  PlaybackFastForwardMode,
  PlaybackInterruptedMode,
  PlaybackOptions,
  PlaybackRepeatedMode,
  PlaybackRewindMode,
  PlaybackStatistics,
  Player,
  PlayerContext,
  Promo,
  RequestType,
  SeekRangeInfo,
  StreamTimelineEvent,
  StreamTimelineMetadata,
  ThumbnailPreviewMetadata,
  TrackInfo,
  TrackType,
  TrackVariantInfo,
  AdTechnology,
} from '@quickplay/player-interface';

import { PromoPlaybackPolicyHandler } from './PromoPlayer/internal/policy/PromoPlaybackPolicyHandler';
import {
  getAdvertBreaksDurationPriorTo,
  getCurrentAdEndTime,
  getSeekPositionFor,
  isAdBreakEndEvent,
  isAdBreakStartEvent,
  isAdEndEvent,
  isAdStartEvent,
  prepareAdBreak,
  getCuePointsRelativeToContentTime,
} from './BrightcovePlayerUtil';
import { BrightcoveDataCollectionConfig } from '../model/BrightcoveDataCollectionConfig';
import { BrightcoveDataCollectionAdapter } from '../model/BrightcoveDataCollectionAdapter';
import { DefaultBrightcoveDataCollectionAdapter } from './DefaultBrightcoveDataCollectionAdapter';

export const TIME_THRESHOLD = 0.001;

export function createDefaultBrightcoveAdsPlayer(
  player: Player,
  logger: Logger,
  adCueMetadata?: Promo[],
  adPlaybackPolicy?: AdPlaybackPolicy,
  dataCollectionConfig?: BrightcoveDataCollectionConfig,
): AdComposedPlayer {
  const contentPlayer: Player = player;
  let adPlaybackPolicyHandler: AdPlaybackPolicyHandler;
  let currentTime_ = 0;
  let duration_ = NaN;
  let playerContext_: PlayerContext = PlayerContext.MAIN;
  let dataCollectionAdapter: BrightcoveDataCollectionAdapter | undefined;

  const eventEmitter: EventEmitter = createEventEmitter();
  let ads: Ad[] | undefined;
  let adBreaks: AdBreak[] | undefined;
  let currentAdBreak_: AdBreak | undefined;
  let currentAd_: Ad | undefined;
  let shouldReportAdProgress = false;
  /**
   * The last reported playhead position. When playback starts the current position is 0 so previousPlayhead
   * is set to -TIME_THRESHOLD instead of undefined.
   *
   * This is useful to determine whether there is a recent promo event such as ad break started, ad break ended, ad
   * started or ad ended. The way this is determined is by comparing the player state at previousPlayhead position
   * and the player state at current playhead position.
   *
   */
  let previousPlayhead: number = -TIME_THRESHOLD;

  function addAdPlaybackPolicyHandler(): void {
    if (adPlaybackPolicy) {
      adPlaybackPolicyHandler = new PromoPlaybackPolicyHandler(contentPlayer, adPlaybackPolicy, logger);
    } else {
      const defaultAdPlaybackPolicy = {
        fastForwardMode: PlaybackFastForwardMode.TRICK_FW_PLAY_LAST,
        rewindMode: PlaybackRewindMode.TRICK_RW_SKIP_ALL,
        autoSeekMode: PlaybackAutoSeekMode.AUTO_SEEK_PLAY_PREROLL,
        repeatMode: PlaybackRepeatedMode.REPEAT_ALWAYS_PLAY,
        interruptMode: PlaybackInterruptedMode.INTERRUPT_RESUME,
      };

      adPlaybackPolicyHandler = new PromoPlaybackPolicyHandler(contentPlayer, defaultAdPlaybackPolicy, logger);
    }
  }

  function addDataCollectionAdapter(player: AdComposedPlayer): void {
    if (dataCollectionConfig) {
      // Create adapter - it will manage all event subscriptions internally
      // Pass brightcovePlayer (AdComposedPlayer) so adapter gets correct context
      dataCollectionAdapter = new DefaultBrightcoveDataCollectionAdapter(dataCollectionConfig, player, logger);
    }
  }

  const brightcovePlayer: AdComposedPlayer = {
    subscribe(eventName: string | eventObject, callback?: AnyFunc): void {
      contentPlayer.subscribe(eventName, callback);
      eventEmitter.subscribe(eventName, callback);
    },

    unsubscribe(eventName: string | eventObject, callback?: AnyFunc): void {
      contentPlayer.unsubscribe(eventName, callback);
      eventEmitter.unsubscribe(eventName, callback);
    },

    get playerContext(): PlayerContext {
      if (playerContext_ !== PlayerContext.AD && contentPlayer && instanceOfAdComposedPlayer(contentPlayer)) {
        return contentPlayer.playerContext;
      }

      return playerContext_;
    },

    get playerState(): string {
      return contentPlayer.playerState;
    },

    get playbackRate(): number {
      return contentPlayer.playbackRate;
    },

    set playbackRate(rate) {
      contentPlayer.playbackRate = rate;
    },

    get availableBitrates(): number[] {
      return contentPlayer.availableBitrates;
    },

    set minBitrate(minBitrate) {
      contentPlayer.minBitrate = minBitrate;
    },

    get minBitrate(): number | undefined {
      return contentPlayer.minBitrate;
    },

    set maxBitrate(maxBitrate) {
      contentPlayer.maxBitrate = maxBitrate;
    },

    get maxBitrate(): number | undefined {
      return contentPlayer.maxBitrate;
    },

    get internalPlayerName(): string {
      return contentPlayer.internalPlayerName;
    },

    get internalPlayerVersion(): string {
      return contentPlayer.internalPlayerVersion;
    },

    get rawPlayer(): any {
      return contentPlayer.rawPlayer;
    },

    get rawLibrary(): any {
      return contentPlayer.rawLibrary;
    },

    get isBuffering(): boolean {
      return contentPlayer.isBuffering;
    },

    get isSeeking(): boolean {
      return contentPlayer.isSeeking;
    },

    get currentTime(): number {
      return adPlaybackPolicyHandler.getCurrentTime(currentAdBreak_, ads);
    },

    get currentEpochTime(): number {
      return contentPlayer.currentEpochTime;
    },

    get duration(): number {
      return adPlaybackPolicyHandler.getDuration(currentAdBreak_, ads);
    },

    get playbackStatistics(): PlaybackStatistics | undefined {
      return contentPlayer.playbackStatistics;
    },

    get isLive(): boolean {
      return contentPlayer.isLive;
    },

    get remainingTime(): number {
      // if this is playing a promo means this is a PROMO player so we
      // return the current promo remaining time based on current promo
      // duration and the player current time
      if (playerContext_ === PlayerContext.AD && currentAd_) {
        return currentAd_.duration - currentTime_;
      }

      //   // if the underlying player is an AdPlayer (IMAPlayer, YospacePlayer, etc.)
      //   // then we just forward this call to the underlying player
      if (isAdPlayer(contentPlayer)) {
        return contentPlayer.remainingTime;
      }

      // this is not an AdPlayer so there's no remaining time
      return 0;
    },

    get isMute(): boolean | undefined {
      return contentPlayer.isMute;
    },

    get playbackOptions(): PlaybackOptions | undefined {
      return contentPlayer.playbackOptions;
    },

    get volume(): number {
      return contentPlayer.volume;
    },

    set volume(volumeLevel: number) {
      contentPlayer.volume = volumeLevel;
    },

    get adInsertionType(): AdTechnology {
      return AdTechnology.SSAI;
    },

    load(): void {
      contentPlayer.load();
    },

    setRequestInterceptor(requestInterceptor: (requestType: RequestType, request: any) => void): void {
      contentPlayer.setRequestInterceptor(requestInterceptor);
    },

    setResponseInterceptor(responseInterceptor: (requestType: RequestType, response: any) => void): void {
      contentPlayer.setResponseInterceptor(responseInterceptor);
    },

    accessFunctionHandler(json: string): void {
      contentPlayer.accessFunctionHandler(json);
    },

    stop(): Promise<void> {
      return contentPlayer.stop();
    },

    play(): void {
      contentPlayer.play();
    },

    pause(): void {
      contentPlayer.pause();
    },

    abort: (error: Error): void => {
      contentPlayer.abort(error);
    },

    send(eventName: StreamTimelineEvent, action: Action, metadata?: StreamTimelineMetadata): void {
      contentPlayer.send(eventName, action, metadata);
    },

    seek(seekPoint: number): void {
      contentPlayer.seek(
        adPlaybackPolicyHandler.willSeekTo(getSeekPositionFor(seekPoint, adBreaks ?? [], contentPlayer.duration)),
      );
    },

    getThumbnail(position: number): Promise<ThumbnailPreviewMetadata | undefined> {
      return contentPlayer.getThumbnail(position);
    },

    seekToLiveEdge(): void {
      return contentPlayer.seekToLiveEdge();
    },

    seekableRange(): SeekRangeInfo {
      if (contentPlayer.isLive) {
        return contentPlayer.seekableRange();
      }

      return {
        start: 0,
        end: this.duration,
      };
    },

    currentOffsetFromLiveEdge(): number {
      return contentPlayer.currentOffsetFromLiveEdge();
    },

    getAllTracks(): TrackInfo[] {
      return contentPlayer.getAllTracks();
    },

    getActiveTracks(): TrackInfo[] {
      return contentPlayer.getActiveTracks();
    },

    getVariantTracks(): TrackVariantInfo[] {
      return contentPlayer.getVariantTracks();
    },

    getAvailableTrackVariants(trackType: TrackType): TrackVariantInfo[] {
      return contentPlayer.getAvailableTrackVariants(trackType);
    },

    selectTrack(track?: TrackInfo): void {
      contentPlayer.selectTrack(track);
    },

    muteOrUnmute(): void {
      contentPlayer.muteOrUnmute && contentPlayer.muteOrUnmute();
    },

    skipAd(): void {
      if (contentPlayer.isLive || !currentAd_) {
        return;
      }

      const skipOffset = getCurrentAdEndTime(currentAd_, ads);
      contentPlayer.seek(skipOffset);
    },

    resize(_width: number, _height: number, _isFullscreen: boolean): void {
      // no-op
    },

    discardAdBreak(): void {
      adPlaybackPolicyHandler.discardAdBreak(currentAdBreak_);
    },

    getCuePoints(): number[] {
      return !adBreaks ? [] : getCuePointsRelativeToContentTime(contentPlayer.duration, adBreaks);
    },

    setPlaybackOptions(playbackOptions: PlaybackOptions): void {
      contentPlayer.setPlaybackOptions(playbackOptions);
    },

    restrictQuality(enableRestriction: boolean): void {
      contentPlayer.restrictQuality(enableRestriction);
    },

    availableMimeTypes(trackType: TrackType): Array<string> {
      return contentPlayer.availableMimeTypes(trackType);
    },

    unloadAndSavePreload(): Promise<void> {
      return contentPlayer.unloadAndSavePreload();
    },

    restoreFromPreload(): void {
      contentPlayer.restoreFromPreload();
    },

    setPreferredMimeTypes(trackType: TrackType, mimeTypes: Array<string>): void {
      return contentPlayer.setPreferredMimeTypes(trackType, mimeTypes);
    },
  };

  function initialize(): void {
    if (adCueMetadata && adCueMetadata.length === 0) {
      return;
    }
    const { adBreaks_, ads_ } = prepareAdBreak(adCueMetadata, duration_);

    adBreaks = adBreaks_;
    ads = ads_;

    adPlaybackPolicyHandler.setAdBreaks(adBreaks ?? []);

    subscribeToInternalPlayerEvents();

    initializeWithInitialPlaybackTime();
  }

  function isAdPlayer(player_: Player): player_ is AdPlayer {
    return 'remainingTime' in player_;
  }

  function instanceOfAdComposedPlayer(player_: Player): player_ is AdComposedPlayer {
    return 'playerContext' in player_;
  }

  /**
   * Searches the player current time for ad related events.
   */
  const evaluateAdEvents = async (): Promise<void> => {
    const adBreak = isAdBreakStartEvent(currentTime_, previousPlayhead, adBreaks);
    // Checks if the adBreak has to repeat or not based the repeat playback policy rule.
    // Skip the adBreak if the the repeat rule is set to play once and the adBreak is already watched.
    if (adBreak && adPlaybackPolicyHandler.playbackRepeatedRule(adBreak) !== true) {
      player.seek(adBreak.startTime + adBreak.duration);
      return;
    }

    evaluateAdBreakStartedEvent();

    evaluateAdEndedEvent();

    evaluateAdStartedEvent();

    evaluateAdBreakEndedEvent();
  };
  /**
   * Event listener for 'playbackstatechanged' event.
   * @param playbackState
   */
  const onPlaybackStateChanged = (playbackState: PLAYBACK_STATES): void => {
    switch (playbackState) {
      case PLAYBACK_STATES.IDLE:
      case PLAYBACK_STATES.STOPPED: {
        unsubscribeFromInternalPlayerEvents();
        // Clean up data collection adapter
        if (dataCollectionAdapter) {
          dataCollectionAdapter.dispose();
          dataCollectionAdapter = undefined;
        }
        break;
      }
      case PLAYBACK_STATES.LOADED: {
        const cuePoints = brightcovePlayer.getCuePoints();

        if (cuePoints) {
          eventEmitter.publish('adcuepoints', cuePoints);
        }

        break;
      }
      default: {
        // ignore
      }
    }
  };

  /**
   * Event listener for 'progressupdate' event.
   */
  const onProgressUpdate = (): void => {
    if (currentAd_ && shouldReportAdProgress) {
      const progress = contentPlayer.currentTime - currentAd_.start;
      eventEmitter.publish('adprogressupdate', currentAd_, progress);
    }

    currentTime_ = contentPlayer.currentTime;
    duration_ = contentPlayer.duration;

    evaluateAdEvents();

    previousPlayhead = currentTime_;
  };

  function subscribeToInternalPlayerEvents(): void {
    contentPlayer.subscribe('playbackstatechanged', onPlaybackStateChanged);
    contentPlayer.subscribe('progressupdate', onProgressUpdate);
  }

  /**
   * Unsubscribe this Promo player from the internal player events
   */
  function unsubscribeFromInternalPlayerEvents(): void {
    contentPlayer.unsubscribe('playbackstatechanged', onPlaybackStateChanged);
    contentPlayer.unsubscribe('progressupdate', onProgressUpdate);
  }

  function initializeWithInitialPlaybackTime(): void {
    if (
      brightcovePlayer.isLive ||
      !contentPlayer.playbackOptions ||
      !contentPlayer.playbackOptions.initialPlaybackTime
    ) {
      return;
    }

    // this is the initial playback position with PREROLL ads duration + player initial playback time
    let initialPlaybackTimeWithAds = getSeekPositionFor(
      contentPlayer.playbackOptions.initialPlaybackTime,
      adBreaks ?? [],
    );

    initialPlaybackTimeWithAds = adPlaybackPolicyHandler.willSeekTo(initialPlaybackTimeWithAds);

    logger.log(`[PROMO] overriding initial playback time`, initialPlaybackTimeWithAds);

    brightcovePlayer.setPlaybackOptions({
      ...contentPlayer.playbackOptions,
      initialPlaybackTime: initialPlaybackTimeWithAds,
    });
  }

  /**
   * Determines if an Ad Break start event ocurred. If an Ad Break started then this will
   * change the player's context and emit the corresponding events to notify the client.
   */
  function evaluateAdBreakStartedEvent(): void {
    const adBreak = isAdBreakStartEvent(currentTime_, previousPlayhead, adBreaks);

    if (adBreak) {
      logger.log('[AD] onAdvertBreakStart', adBreak);
      playerContext_ = PlayerContext.AD;
      eventEmitter.publish('playercontextchanged', playerContext_);
      currentAdBreak_ = adBreak;
      eventEmitter.publish(AdEvent.ADVERT_BREAK_START, currentAdBreak_);
    }
  }

  /**
   * Determines if an Ad start event ocurred. If an Ad started then this will
   * emit the corresponding event to notify the client.
   */
  function evaluateAdStartedEvent(): void {
    const ad = isAdStartEvent(currentTime_, previousPlayhead, adBreaks, ads);

    if (ad) {
      logger.log('[AD] onAdvertStart', ad);

      shouldReportAdProgress = true;
      currentAd_ = ad;
      eventEmitter.publish(AdEvent.ADVERT_START, {
        ...currentAd_,
        contentPlayheadPositionMs:
          currentAdBreak_?.position === AdContentTimePosition.PREROLL
            ? 0
            : currentAdBreak_?.position === AdContentTimePosition.POSTROLL
              ? currentAd_.contentDurationMs
              : Math.ceil(
                  (contentPlayer.currentTime -
                    getAdvertBreaksDurationPriorTo(contentPlayer.currentTime, adBreaks ?? [], true)) *
                    1000,
                ),
      });
    }
  }

  /**
   * Determines if an Ad ended event ocurred. If an Ad ended then this will
   * emit the corresponding event to notify the client.
   */
  function evaluateAdEndedEvent(): void {
    const ad = isAdEndEvent(currentTime_, previousPlayhead, adBreaks, ads);

    if (ad) {
      logger.log('[AD] onAdvertEnd');
      shouldReportAdProgress = false;
      eventEmitter.publish(AdEvent.ADVERT_END, {
        ...currentAd_,
        contentPlayheadPositionMs:
          currentAdBreak_?.position === AdContentTimePosition.PREROLL
            ? 0
            : Math.ceil(
                (contentPlayer.currentTime -
                  getAdvertBreaksDurationPriorTo(contentPlayer.currentTime, adBreaks ?? [], true)) *
                  1000,
              ),
      });
      currentAd_ = undefined;
    }
  }

  /**
   * Determines if an Ad Break ended event ocurred. If an Ad Break ended then this will
   * change the player's context and emit the corresponding events to notify the client.
   */
  function evaluateAdBreakEndedEvent(): void {
    const adBreak = isAdBreakEndEvent(currentTime_, previousPlayhead, adBreaks, currentTime_, adPlaybackPolicyHandler);

    if (adBreak) {
      logger.log('[AD] onAdvertBreakEnd');
      playerContext_ = PlayerContext.MAIN;
      eventEmitter.publish('playercontextchanged', playerContext_);
      currentAdBreak_ = undefined;
      eventEmitter.publish(AdEvent.ADVERT_BREAK_END);
    }
  }
  addAdPlaybackPolicyHandler();
  addDataCollectionAdapter(brightcovePlayer);
  initialize();
  return brightcovePlayer;
}
